import { client } from '@cockpitai/lib/api'
import { TFidelityResponse } from '@cockpitai/pages/dashboard/Fidelity/types'
import { TCommonResponse } from '../common'
import {
  ModuleType,
  TAchievementRVPanel,
  TAffiliateDetails,
  TAffiliateNavigation,
  TBankingMarket,
  TBigNumbers,
  TBigQuestionCardsData,
  TBigQuestionChartType,
  TBigQuestionsCards,
  TBigViewsCards,
  TBigViewsCardsData,
  TBigViewsChartType,
  TBigViewsPieCardsData,
  TBridgeMomData,
  TCommonParams,
  TConsolidated,
  TContractSigned,
  TConversionsCards,
  TConversionsCardsData,
  TConversionsListType,
  TFPD10VsProductionData,
  TFunnelConversionData,
  TGeneralViewByMonthResponse,
  THeatMapByBusinessData,
  THourByHour,
  TKPISFidelity,
  TLeadersData,
  TLoyalStoresByNetwork,
  TMarketMoM,
  TMarketShareCards,
  TMixBySegment,
  TMktShareElectricCars,
  TMktShareMonth,
  TMyPanels,
  TOpportunitiesListResponse,
  TOpportunitiesResponse,
  TOrderedCardItem,
  TPanelsPeople,
  TProductionChartData,
  TProductionFinancialData,
  TProposals,
  TPropostasAprovadasNaoEfetivadas,
  TPropostasAprovadasNaoEfetivadasMetrics,
  TPropostasPagas,
  TPropostasPagasMetrics,
  TRadarBigNumber,
  TRadarCards,
  TResumeApnefData,
  TResumeOfDayResponse,
  TRVCardsData,
  TRVChartsCardsType,
  TSantanderMoM,
  TSpreadData,
  TStaffData,
  TSummaryVisionItem,
  TTabSummaryMeta,
  TTabSummaryStore,
  TUsersHierarchy,
  TWallet
} from './types'

export async function getResumeOfDay() {
  const response = await client.get<TResumeOfDayResponse>(
    '/panels/resume-of-day',
  )

  return response.data
}

export function getHeatMapProductionByBusiness(params: {
  affiliateId?: string
}) {
  return client
    .get<THeatMapByBusinessData>('/panels/production-by-business', { params })
    .then((response) => response.data)
}

export function getRadarBigNumbers(params: TCommonParams) {
  return client
    .get<TRadarBigNumber[]>('/panels/radar-big-numbers', { params })
    .then((response) => response.data)
}

export function getSummaryVisionByBusiness({
  vision,
  ...params
}: TCommonParams & { vision: string }) {
  return client
    .get<TSummaryVisionItem[]>(`/panels/summary-vision/${vision}`, { params })
    .then((response) => response.data)
}

export function getFunnelConversion(params: TCommonParams) {
  return client
    .get<TFunnelConversionData>('/panels/funnel-conversion', { params })
    .then((response) => response.data)
}

export function getResumeApnef(params: TCommonParams) {
  return client
    .get<TResumeApnefData>('/panels/resume-apnef', { params })
    .then((response) => response.data)
}

export function getProposalResumeApnef(params: TCommonParams) {
  return client
    .get<TResumeApnefData>('/panels/proposals/resume-apnef', { params })
    .then((response) => response.data)
}

export function getAnchievementRv(params: TCommonParams) {
  return client
    .get<TAchievementRVPanel>('/panels/anchievement-rv', {
      params,
    })
    .then((response) => response.data)
}

export function getProductionFinancial(params: TCommonParams) {
  return client
    .get<
      TCommonResponse<TProductionFinancialData[]>
    >('/panels/production-financial', { params })
    .then((response) => response.data)
}

export function getStaff(params: TCommonParams) {
  return client
    .get<TStaffData>('/panels/staff', { params })
    .then((response) => response.data)
}

export function getMeshProduction() {
  return client
    .get<TCommonResponse<TOrderedCardItem[]>>('/panels/mesh-production')
    .then((response) => response.data)
}

export function getGRProduction() {
  return client
    .get<TCommonResponse<TOrderedCardItem[]>>('/panels/gr-production')
    .then((response) => response.data)
}

export function getBaiiPerWallet() {
  return client
    .get<TCommonResponse<TOrderedCardItem[]>>('/panels/baii-per-wallet')
    .then((response) => response.data)
}

export function getContractsSigned(params: TCommonParams) {
  return client
    .get<
      TCommonResponse<TContractSigned[]>
    >('/panels/big-views/contracts-signed', { params })
    .then((response) => response.data)
}

export function getFpd10VsProduction(params: TCommonParams) {
  return client
    .get<
      TCommonResponse<TFPD10VsProductionData>
    >('/panels/big-views/fpd10-vs-production', { params })
    .then((response) => response.data)
}

export function getBridgeMoM(params: TCommonParams) {
  type TBridgeMomDataReturn = {
    data: TBridgeMomData
  }

  return client
    .get<TBridgeMomDataReturn>('panels/market/conversion/bridge-mom', {
      params,
    })
    .then((response) => {
      return response.data ? response.data.data : ({} as TBridgeMomData)
    })
}

export function getSpread(params: TCommonParams) {
  return client
    .get<TCommonResponse<TSpreadData[]>>('/panels/spread', { params })
    .then((response) => response.data)
}

export function getLeaders(params: TCommonParams) {
  return client
    .get<TCommonResponse<TLeadersData[]>>('/panels/leaders', {
      params,
    })
    .then((response) => response.data)
}

export async function getMarketMoM(params: TCommonParams) {
  return client
    .get<TCommonResponse<TMarketMoM>>('/panels/market/share/market-mom/', {
      params,
    })
    .then((response) => response.data)
}

export async function getSantanderMoM(params: TCommonParams) {
  return client
    .get<TCommonResponse<TSantanderMoM>>('panels/market/share/santander-mom', {
      params,
    })
    .then((response) => response.data)
}

export async function getConsolidated(params: TCommonParams) {
  return client
    .get<TCommonResponse<TConsolidated>>(
      'panels/market/share/consolidated-competitors',
      {
        params,
      },
    )
    .then((response) => response.data)
}

export async function getMixBySegment(params: TCommonParams) {
  return client
    .get<
      TCommonResponse<TMixBySegment>
    >('/panels/market/share/mix-by-segment', { params })
    .then((response) => response.data)
}

export async function getMarketPotential(params: TCommonParams) {
  return client
    .get<
      TCommonResponse<TMixBySegment>
    >('/panels/market/share/potential-market-volume', { params })
    .then((response) => response.data)
}

export function getBigQuestionsCards(params: TCommonParams) {
  return client
    .get<TBigQuestionsCards[]>('/panels/big-questions', { params })
    .then((response) => response.data)
}

export function getGroupVision(params: TCommonParams) {
  return client
    .get<unknown>('/panels/group-vision', { params })
    .then((response) => response.data)
}

export function getBigQuestionsCardsData(
  params: TCommonParams,
  type?: TBigQuestionChartType,
) {
  return client
    .get<TBigQuestionCardsData>(`/panels/big-questions/${type}`, { params })
    .then((response) => response.data)
}

export function getBigViewsCards(params: TCommonParams) {
  return client
    .get<TBigViewsCards[]>(`/panels/big-views`, { params })
    .then((response) => {
      return response.data
    })
}

export async function getMyPanelsCards(params: TCommonParams) {
  return client
    .get<TMyPanels[]>(`/users/my-charts`, { params })
    .then((response) => {
      return response.data
    })
}

export function getBigViewsCardsData(
  params: TCommonParams,
  type?: TBigViewsChartType,
) {
  return client
    .get<TBigViewsCardsData>(`/panels/big-views/${type}`, {
      params,
    })
    .then((response) => response.data)
}

export function getBigViewsPieCardsData(params: TCommonParams) {
  return client
    .get<TBigViewsPieCardsData>(`/panels/big-views/loyalty`, {
      params,
    })
    .then((response) => response.data)
}

export async function getAffiliateDetails({
  affiliateId,
  ...params
}: TCommonParams) {
  return client
    .get<TAffiliateDetails>(
      !affiliateId ? '/affiliate' : `/affiliate?affiliateId=${affiliateId}`,
      { params },
    )
    .then((response) => response.data)
}

export async function getMarketShareMonth(params: TCommonParams) {
  return client
    .get<TCommonResponse<TMktShareMonth>>('/panels/market/share/market-month', {
      params,
    })
    .then((response) => response.data)
}

export async function getMarketShareElectricCars(params: TCommonParams) {
  return client
    .get<
      TCommonResponse<TMktShareElectricCars>
    >('/panels/market/share/market-eletric-cars', { params })
    .then((response) => response.data)
}

export async function getBigNumbersPage(params: TCommonParams) {
  return client
    .get<TBigNumbers>('/panels/big-numbers', { params })
    .then((response) => response.data)
}

export async function getBigNumbersByType(
  type: ModuleType,
  params: TCommonParams,
) {
  return client
    .get(`/panels/big-numbers/${type}`, { params })
    .then((response) => response.data)
}

export function getConversionsCards(params: TCommonParams) {
  type TConversionsCardsReturn = {
    list: TConversionsCards[]
  }

  return client
    .get<TConversionsCardsReturn>('panels/market/conversion', { params })
    .then((response) => {
      return response?.data && response.data.list
    })
}

export function getConversionsCardsData(
  params: TCommonParams,
  type?: TConversionsListType,
) {
  return client
    .get<TConversionsCardsData>(`panels/market/conversion/${type}`, { params })
    .then((response) => response.data)
}

export async function getBankingMarket(params: TCommonParams) {
  type TBankingMarketReturn = {
    data: TBankingMarket
  }

  return client
    .get<TBankingMarketReturn>('/panels/market/share/financial-ranking', {
      params,
    })
    .then((response) =>
      response?.data ? response.data.data : ({} as TBankingMarket),
    )
}

export async function getMarketMixSegmentQuantity(params: TCommonParams) {
  return client
    .get<TCommonResponse<TMixBySegment>>(
      '/panels/market/share/mix-by-segment/quantity',
      {
        params,
      },
    )
    .then((response) => response.data)
}

export async function getMarketMixSegmentVolume(params: TCommonParams) {
  return client
    .get<TCommonResponse<TMixBySegment>>(
      '/panels/market/share/mix-by-segment/volume',
      {
        params,
      },
    )
    .then((response) => response.data)
}

export async function getMarketMixSegmentBank(params: TCommonParams) {
  return client
    .get<TCommonResponse<TMixBySegment>>(
      '/panels/market/share/mix-by-segment/bank',
      {
        params,
      },
    )
    .then((response) => response.data)
}

export async function getMarketMixSegmentBankTerm(params: TCommonParams) {
  return client
    .get<TCommonResponse<TMixBySegment>>(
      '/panels/market/share/mix-by-segment/bank-by-term',
      {
        params,
      },
    )
    .then((response) => response.data)
}

export function getRadarCharts(params: TCommonParams) {
  type TRadarCardsReturn = {
    list: TRadarCards[]
  }

  return client
    .get<TRadarCardsReturn>('/panels/radars', { params })
    .then((response) => {
      return response?.data && response.data.list
    })
}

export function getRVCharts(params: TCommonParams) {
  type TRVCardsReturn = {
    title: string
    type: TRVChartsCardsType
  }[]

  return client
    .get<TRVCardsReturn>('/panels/big-variables-revenues/', { params })
    .then((response) => {
      return response.data || []
    })
}

export function getHourByHour() {
  return client
    .get<THourByHour>('/production-reports/daily/last')
    .then((response) => {
      return response.data || []
    })
}

export function getRVChartsCardsData(
  params: TCommonParams,
  type?: TRVChartsCardsType,
) {
  return client
    .get<TRVCardsData>(`/panels/big-variables-revenues/${type}`, { params })
    .then((response) => response.data)
}

export function getMarketShareChartsData(params: TCommonParams) {
  type TMarketShareCardsReturn = {
    list: TMarketShareCards[]
  }

  return client
    .get<TMarketShareCardsReturn>('/panels/market/share', { params })
    .then((response) => {
      return response?.data && response.data.list
    })
}

export async function postAddToPanel(params: TCommonParams) {
  const response = await client.post('/users/my-charts', params)

  return response.data
}

export async function deleteChartById(id: number) {
  const response = await client.delete(`/users/my-charts/${id}`)

  return response.data
}

export async function getPanelsPeopleHierarchy() {
  const response = await client.get<TUsersHierarchy[]>(
    '/users/people/hierarchy',
  )

  return response.data
    .flatMap((item) => {
      const parsed: TPanelsPeople = {
        label: item.name,
        value: item.id,
      }

      return parsed
    })
    .filter((item) => item.label)
}

export async function getProductionChart(params: TCommonParams) {
  return client
    .get<TProductionChartData>('/panels/big-numbers/production-vs-goal', { params })
    .then((response) => response.data)
}

export async function getFidelity(params: string) {
  return client
    .get<TFidelityResponse>(`/panels/fidelity${params && `?${params}`}`)
    .then((response) => {
      return response.data
    })
}

export async function getGeneralViewByMonth(params: string) {
  return client
    .get<TCommonResponse<TGeneralViewByMonthResponse>>(`/panels/fidelity/numbers${params && `?${params}`}`)
    .then((response) => response.data)
}

export async function getKPISFidelity(params: string) {
  return client
    .get<TCommonResponse<TKPISFidelity>>(`/panels/fidelity/kpis${params && `?${params}`}`)
    .then((response) => response.data)
}

export function getWallet(params: string) {
  return client
    .get<TWallet>(`/panels/fidelity/wallet${params && `?${params}`}`)
    .then((response) => {
      return response.data || []
    })
}

export function getOpportunities(params: string) {
  return client
    .get<TOpportunitiesResponse>(`/panels/fidelity/opportunities${params && `?${params}`}`)
    .then((response) => response.data)
}

export function getOpportunitiesList(params: string) {
  return client
    .get<TOpportunitiesListResponse>(`/panels/fidelity/opportunities-list?${params}`)
    .then((response) => response.data)
}

export function getLoyalStores(params: string) {
  return client
    .get<{ data: TRadarBigNumber[] }>(`/panels/fidelity/loyal-stores${params && `?${params}`}`)
    .then((response) => response.data.data)
}

export function getLoyalStoresByNetwork(params: string) {
  return client
    .get<{ data: TLoyalStoresByNetwork[] }>(`/panels/fidelity/loyal-stores-by-network${params && `?${params}`}`)
    .then((response) => response.data.data)
}

export function getAffiliateNavigation(params: string) {
  return client
    .get<{ data: TAffiliateNavigation[] }>(`/panels/fidelity/affiliate-navigation${params && `?${params}`}`)
    .then((response) => response.data.data)
}

export function getPropostasPagas(params: string) {
  return client
    .get<TPropostasPagas>(`/panels/proposals/paid${params && `?${params}`}`)
    .then((response) => {
      return {
        refDate: response.data.refDate,
        data: response.data.data || []
      }
    })
}

export function getPropostasPagasMetrics(params: string) {
  return client
    .get<TPropostasPagasMetrics>(`/panels/proposals/paid-numbers${params && `?${params}`}`)
    .then((response) => {
      return response.data || []
    })
}

export function getPropostasAprovadasNaoEfetivadas(params: string) {
  return client
    .get<TPropostasAprovadasNaoEfetivadas>(`/panels/proposals/not-paid${params && `?${params}`}`)
    .then((response) => {
      return {
        refDate: response.data.refDate,
        data: response.data.data || []
      }
    })
}

export function getPropostasAprovadasNaoEfetivadasMetrics(params: string) {
  return client
    .get<TPropostasAprovadasNaoEfetivadasMetrics>(`/panels/proposals/not-paid-numbers${params && `?${params}`}`)
    .then((response) => {
      return response.data || []
    })
}

export async function getProposalsPage(params: TCommonParams) {
  return client
    .get<TProposals>('/panels/proposals', { params })
    .then((response) => response.data)
}

export function getTabSummaryMeta(params: string): Promise<TTabSummaryMeta> {
  return client
    .get<{ data: TTabSummaryMeta }>(`/panels/fidelity/tab-summary-meta${params && `?${params}`}`)
    .then((response) => response.data.data)
}

export function getTabSummaryStore(params: string): Promise<TTabSummaryStore> {
  return client
    .get<{ data: TTabSummaryStore }>(`/panels/fidelity/tab-summary-store${params && `?${params}`}`)
    .then((response) => response.data.data)
}
